import { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

const CST_STATIC_FORM_ORIGIN = 'https://support-forms-ui.atlassian.com';

const getCstStaticFormRedirect = (value: string): string | null => {
	try {
		const destination = new URL(value, window.location.href);
		if (destination.protocol !== 'https:' || destination.username || destination.password)
			return null;
		if (
			destination.origin !== window.location.origin &&
			destination.origin !== CST_STATIC_FORM_ORIGIN &&
			destination.origin !== 'https://support.atlassian.com'
		)
			return null;
		return destination.href;
	} catch {
		return null;
	}
};

export const CstStaticFormEmbed = ({ formName, height }: { formName: string; height: number }) => {
	const intl = useIntl();
	const iframeRef = useRef<HTMLIFrameElement>(null);

	useEffect(() => {
		const iframe = iframeRef.current;
		if (!iframe) return;

		// Match the CST loader's URL construction without evaluating author HTML or
		// installing its permanent global message listeners.
		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== CST_STATIC_FORM_ORIGIN || event.source !== iframe.contentWindow) return;
			if (typeof event.data !== 'string') return;
			const [action, value] = event.data.split('|');
			if (!value) return;
			if (action === 'resize' && /^[1-9]\d{0,5}$/.test(value) && Number(value) <= 100_000) {
				iframe.height = value;
			} else if (action === 'redirect') {
				const destination = getCstStaticFormRedirect(value);
				if (destination) window.location.replace(destination);
			}
		};
		window.addEventListener('message', handleMessage);
		iframe.src = `${CST_STATIC_FORM_ORIGIN}/forms/${formName}${window.location.search}`;
		return () => window.removeEventListener('message', handleMessage);
	}, [formName]);

	return (
		<iframe
			ref={iframeRef}
			id={`${formName}-iframe`}
			className="cst-static-form-iframe"
			height={height}
			width="100%"
			frameBorder="0"
			scrolling="no"
			title={intl.formatMessage({
				id: 'ThirdPartyEmbed.cstStaticFormTitle',
				defaultMessage: 'Atlassian support form',
			})}
			data-third-party-embed-provider="cstStaticForm"
		/>
	);
};
